import { ArrowLeft, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/api/client';
import EarningsDisclaimer from '@/components/EarningsDisclaimer';

/**
 * ResetPasswordPage — consumes the URL token from /reset-password?token=...
 * and submits a new password to /api/auth/password-reset/confirm.
 */
const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get('token') || '').trim();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    // No token in URL — send user back to the forgot page so they can
    // restart the flow cleanly. Avoids an infinite reset-with-no-token loop.
    return <Navigate to="/forgot-password" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/password-reset/confirm', {
        token,
        new_password: newPassword,
      });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Reset failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 py-10 gap-6" data-testid="reset-password-page">
      <div className="w-full max-w-md sc-glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden" style={{ borderColor: 'rgba(255,182,18,0.32)' }}>
        <div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] hover:text-[#FFC20E] mb-3"
            data-testid="reset-back-link"
          >
            <ArrowLeft className="w-3 h-3" /> Back to login
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center border"
              style={{
                background: 'rgba(255,182,18,0.06)',
                borderColor: 'rgba(255,182,18,0.32)',
              }}
              aria-hidden="true"
            >
              <ShieldCheck className="w-5 h-5 text-[#FFC20E]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Choose a new password</div>
              <h1 className="font-display font-bold text-xl mt-0.5">Reset password</h1>
            </div>
          </div>

          {done ? (
            <div data-testid="reset-success-panel">
              <div
                className="flex items-center gap-3 rounded-xl p-4 border mb-3"
                style={{
                  background: 'rgba(16,185,129,0.08)',
                  borderColor: 'rgba(16,185,129,0.32)',
                }}
              >
                <CheckCircle2 className="w-5 h-5 text-[#10B981] shrink-0" />
                <div className="text-[13px] text-[var(--sc-text-bright)] leading-snug">Password updated. Redirecting you to sign in…</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-[12px] text-[var(--sc-text-dim)] leading-relaxed">
                Choose a strong password (at least 8 characters). The reset link is single-use and will be consumed when you submit.
              </p>
              <div>
                <label htmlFor="new-pw" className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
                  New password
                </label>
                <input
                  id="new-pw"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="sc-input"
                  data-testid="reset-new-password"
                />
              </div>
              <div>
                <label htmlFor="confirm-pw" className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
                  Confirm new password
                </label>
                <input
                  id="confirm-pw"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="sc-input"
                  data-testid="reset-confirm-password"
                />
              </div>
              <button type="submit" disabled={busy} className="sc-btn-white w-full" style={{ padding: '12px 22px', fontSize: 13 }} data-testid="reset-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Update password
              </button>
            </form>
          )}
        </div>
      </div>
      <EarningsDisclaimer variant="compact" />
    </main>
  );
};

export default ResetPasswordPage;
